import styled from '@mui/material/styles/styled';
import { colors } from '@sweep-io/sweep-design';
import { memo, useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import { useSelector } from 'react-redux';
import { MarkdownWrapper } from '../../MarkdownWrapper';
import { MarkdownWithReply } from '../../MarkdownWithReply';
import { useRunOnce } from '../../useRunOnce';
import { PdfExportButton } from './PdfExportButton';
import { RichTextCopyButton } from './RichTextCopyButton';
import { MessageFeedbackButtons } from './MessageFeedbackButtons';
import { DataTableVariant } from '../../table/TableTypes';
import { ReasoningSection } from './ReasoningSection';
import { useTypewriter } from '../../../../hooks/useTypewriter';
import { AiChatMessage } from '../aiChatTypes';
import {
  getTextContent,
  getArtifactParts,
  getTableParts,
  parseTextWithTablePlaceholders,
} from '../aiChatHelpers';
import { ArtifactPreview } from '../../artifacts/ArtifactPreview';
import { TableArtifactPreview } from '../../artifacts/TableArtifactPreview';
import { highlightMatchInMarkdown } from '../../../../lib/highlightMatchInMarkdown';
import { useArtifactsOptional } from '../../artifacts/useArtifacts';
import {
  selectActiveChatId,
  selectTableMetadataMap,
} from '../../../aiAgentCenter/agentsChatsReducer';
import { useAgentChatInstanceOptional } from '../../../aiAgentCenter/AgentChatInstanceContext';
import { ChatInstance } from '../../../aiAgentCenter/ChatInstance';
import useSendBiEvent from '../../../../hooks/useSendBiEvent';
import { AI_AGENTS_EVENTS } from '../../../../services/events';

const MESSAGE_STYLE = {
  pr: 1.5,
  pl: 1.5,
};

const AiChatAssistantMessageBoxComponent = ({
  message,
  onMount,
  onTextSelection,
  isStreamingResponse = false,
  chatName,
  dataTableVariant = DataTableVariant.small,
  onAnimationProgressPersistence,
  searchTermToHighlight,
  artifactVersionMap,
  chatId,
  hideActionButtons = false,
}: {
  message: AiChatMessage;
  onMount?: () => void;
  onTextSelection?: (text: string | null) => void;
  isStreamingResponse?: boolean;
  chatName?: string;
  dataTableVariant?: DataTableVariant;
  onAnimationProgressPersistence?: (progress: {
    messageCharIndex: number;
    reasoningChunkIndex: number;
  }) => void;
  searchTermToHighlight?: string;
  artifactVersionMap?: Map<string, number>;
  chatId?: string;
  hideActionButtons?: boolean;
}) => {
  const content = message.content ?? '';
  const reasoningChunks = message.reasoningChunks;
  const errorState = message.isError ?? false;
  const artifactContext = useArtifactsOptional();
  const artifacts = artifactContext?.artifacts ?? {};

  // Get table metadata map for resolving placeholder IDs to actual tableIds
  const chatInstanceContext = useAgentChatInstanceOptional();
  const chatInstanceId = chatInstanceContext?.chatInstanceId ?? ChatInstance.agentsPage;
  const activeChatId = useSelector(selectActiveChatId(chatInstanceId));
  const tableMetadataMap = useSelector(selectTableMetadataMap(activeChatId ?? ''));

  // Check for artifacts in two ways:
  // 1. Real-time streaming: contentType === 'artifact' with artifactData
  // 2. Historical from DB: structured content with artifact parts
  const isStreamingArtifact = message.contentType === 'artifact' && message.artifactData;
  const historicalArtifacts = getArtifactParts(content);
  const historicalTables = getTableParts(content);
  const hasArtifacts = isStreamingArtifact || historicalArtifacts.length > 0;
  const hasStructuredContent = hasArtifacts || historicalTables.length > 0;

  const messageRef = useRef<HTMLDivElement>(null);
  const resumeMessageAnimationFromPosition = message?.animationProgress?.messageCharIndex || 0;
  const resumeReasoningChunkFromIndex = message?.animationProgress?.reasoningChunkIndex || 0;
  const [isOrWasStreaming] = useState(isStreamingResponse);

  // Track current progress for unmount persistence
  const currentMessageCharRef = useRef(resumeMessageAnimationFromPosition);
  const currentReasoningChunkRef = useRef(resumeReasoningChunkFromIndex);

  // Persist animation progress when component unmounts
  useEffect(() => {
    return () => {
      // Only persist if we were actually animating and callback is provided
      if ((isStreamingResponse || reasoningChunks) && onAnimationProgressPersistence) {
        onAnimationProgressPersistence({
          messageCharIndex: currentMessageCharRef.current,
          reasoningChunkIndex: currentReasoningChunkRef.current,
        });
      }
    };
  }, [isStreamingResponse, reasoningChunks, onAnimationProgressPersistence]);

  const handleProgressUpdate = useCallback((characterIndex: number) => {
    currentMessageCharRef.current = characterIndex;
  }, []);

  const handleReasoningChunkProgress = useCallback((chunkIndex: number) => {
    currentReasoningChunkRef.current = chunkIndex;
  }, []);

  // Extract text content (handles both string and structured content)
  const textContent = getTextContent(content);

  // Apply search highlighting if needed
  const highlightedTextContent = searchTermToHighlight
    ? highlightMatchInMarkdown(textContent, searchTermToHighlight)
    : textContent;

  const { displayedText, isComplete: isTypewriterComplete } = useTypewriter(
    isStreamingResponse ? highlightedTextContent : '',
    {
      autoStart: isStreamingResponse,
      supportStreaming: true,
      resumeFromPosition: resumeMessageAnimationFromPosition,
      onProgressUpdate: handleProgressUpdate,
    },
  );

  // Determine the actual content to display
  const displayContent = isStreamingResponse ? displayedText : highlightedTextContent;

  // Don't show buttons bar for messages with artifacts/tables, when explicitly hidden,
  // or while streaming animation is in progress
  const shouldShowButtonsBar = (() => {
    if (hideActionButtons || hasStructuredContent) {
      return false;
    }
    if (isOrWasStreaming) {
      return !isStreamingResponse && isTypewriterComplete;
    }
    return true;
  })();

  useRunOnce(() => {
    onMount?.();
  });

  const markdownWrapperProps = {
    content: displayContent,
    color: errorState ? colors.blush[600] : undefined,
    removeMargins: true,
    dataTableVariant,
    shouldShowTableCopyButtons: shouldShowButtonsBar,
  };

  // Parse streaming text for table placeholders [[TABLE:xxx]]
  const parsedStreamingContent = useMemo(() => {
    if (typeof displayContent === 'string') {
      return parseTextWithTablePlaceholders(displayContent);
    }
    return [];
  }, [displayContent]);

  // Check if streaming content contains table placeholders
  const hasTablePlaceholders = parsedStreamingContent.some((segment) => segment.type === 'table');
  const sendBiEvent = useSendBiEvent();
  const sendExportPdfEvent = () => {
    sendBiEvent({ name: AI_AGENTS_EVENTS.exportToPdfFromChat });
  };
  const sendCopyTextEvent = () => {
    sendBiEvent({ name: AI_AGENTS_EVENTS.copyMessageText });
  };

  const renderContent = () => {
    // Real-time streaming artifact (shows text + artifact box)
    if (isStreamingArtifact) {
      return (
        <Stack gap={1}>
          {displayContent && (
            <MarkdownWrapper {...markdownWrapperProps} shouldShowTableCopyButtons={false} />
          )}
          <ArtifactPreview artifactData={message.artifactData!} />
        </Stack>
      );
    }

    // Real-time streaming with table placeholders [[TABLE:xxx]]
    if (hasTablePlaceholders && typeof displayContent === 'string') {
      return (
        <Stack gap={1}>
          {parsedStreamingContent.map((segment, idx) => {
            switch (segment.type) {
              case 'text':
                if (!segment.content.trim()) return null;
                return (
                  <MarkdownWrapper
                    key={`text-${idx}`}
                    content={segment.content}
                    color={errorState ? colors.blush[600] : undefined}
                    removeMargins={true}
                    dataTableVariant={dataTableVariant}
                    shouldShowTableCopyButtons={false}
                  />
                );
              case 'table': {
                // Resolve placeholder ID to actual tableId using the metadata map
                const actualTableId = tableMetadataMap?.[segment.tableId] ?? segment.tableId;
                return <TableArtifactPreview key={`table-${idx}`} tableId={actualTableId} />;
              }
              default:
                return null;
            }
          })}
        </Stack>
      );
    }

    // Historical artifacts/tables with structured content (render in order)
    if ((historicalArtifacts.length > 0 || historicalTables.length > 0) && Array.isArray(content)) {
      return (
        <Stack gap={1}>
          {content.map((part, idx) => {
            switch (part.type) {
              case 'text': {
                // Apply highlighting to text parts
                const textContent = searchTermToHighlight
                  ? highlightMatchInMarkdown(part.text, searchTermToHighlight)
                  : part.text;

                return (
                  <MarkdownWrapper
                    key={`text-${idx}`}
                    content={textContent}
                    color={errorState ? colors.blush[600] : undefined}
                    removeMargins={true}
                    dataTableVariant={dataTableVariant}
                    shouldShowTableCopyButtons={false}
                  />
                );
              }
              case 'artifact': {
                // Get version number from version map (based on order in chat)
                // or from stored artifact if it was already fetched
                const existingArtifact = artifacts?.[part.artifactId];
                const versionFromStore = existingArtifact?.versions.find(
                  (v) => v.versionId === part.versionId,
                )?.version;
                const versionFromMap = artifactVersionMap?.get(part.versionId);
                const version = versionFromStore ?? versionFromMap;

                return (
                  <ArtifactPreview
                    key={`artifact-${idx}`}
                    artifactData={{
                      artifactId: part.artifactId,
                      title: part.title.replace(/^"|"$/g, ''), // Remove surrounding quotes
                      versionId: part.versionId,
                      version: version,
                    }}
                  />
                );
              }
              case 'table':
                return <TableArtifactPreview key={`table-${idx}`} tableId={part.tableId} />;
              default:
                return null;
            }
          })}
        </Stack>
      );
    }

    // No artifacts - render normally
    if (onTextSelection) {
      return (
        <MarkdownWithReply
          markdownWrapperProps={markdownWrapperProps}
          onTextSelection={onTextSelection}
        />
      );
    }

    return <MarkdownWrapper {...markdownWrapperProps} />;
  };

  return (
    <Stack gap={1} data-message-type="assistant" data-testid="ai-chat-assistant-message-box">
      <Box sx={MESSAGE_STYLE}>
        {reasoningChunks && (
          <ReasoningSection
            reasoningChunks={reasoningChunks}
            isStreamingReasoning={isStreamingResponse && !content}
            resumeFromChunkIndex={resumeReasoningChunkFromIndex}
            onChunkProgressUpdate={handleReasoningChunkProgress}
          />
        )}
      </Box>

      <Box
        display="flex"
        gap={0.5}
        alignItems="flex-start"
        width={hasStructuredContent || hasTablePlaceholders ? '100%' : undefined}
      >
        <AssistantMessageBox
          className="message-content-box"
          ref={messageRef}
          sx={{
            ...(hasStructuredContent || hasTablePlaceholders ? {} : MESSAGE_STYLE),
            ...((hasStructuredContent || hasTablePlaceholders) && {
              flex: 1,
              width: '100%',
            }),
          }}
        >
          {renderContent()}
        </AssistantMessageBox>
      </Box>
      {shouldShowButtonsBar && (
        <Box
          className="buttons-bar"
          display="flex"
          alignItems="center"
          gap={0.5}
          sx={{
            ml: '6px', //the button's icon needs to be aligned to the message's text
          }}
        >
          <RichTextCopyButton messageRef={messageRef} onClickCb={sendCopyTextEvent} />
          {chatId && message.runId && (
            <MessageFeedbackButtons
              chatId={chatId}
              runId={message.runId}
              currentFeedback={message.feedback}
            />
          )}
          <PdfExportButton
            messageRef={messageRef}
            chatName={chatName}
            onClickCb={sendExportPdfEvent}
          />
        </Box>
      )}
    </Stack>
  );
};

const AssistantMessageBox = styled(Box)({
  maxWidth: '100%',
});

export const AiChatAssistantMessageBox = memo(AiChatAssistantMessageBoxComponent);
